import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { get, post, put } from "../utils/networkUtils";

// ── Action definitions supported by the scoring engine ──
const PREDEFINED_ACTIONS = [
  // Core — All Players
  {
    key: "appearance",
    label: "Appearance Points",
    desc: "Points for playing up to the appearance threshold",
    category: "core_all",
  },
  {
    key: "appearance_60",
    label: "Appearance Bonus",
    desc: "Bonus points for reaching the appearance bonus threshold",
    category: "core_all",
  },
  {
    key: "goal_scored",
    label: "Goal Scored",
    desc: "Any goal scored by the player",
    category: "core_all",
  },
  {
    key: "assist",
    label: "Assist",
    desc: "Direct assist to a goal",
    category: "core_all",
  },
  {
    key: "shot_on_target",
    label: "Shot on Target",
    desc: "Shot that requires a save",
    category: "core_all",
  },
  {
    key: "big_chance_created",
    label: "Big Chance Created",
    desc: "Created a clear scoring opportunity",
    category: "core_all",
  },
  {
    key: "tackles",
    label: "Tackles Bonus",
    desc: "Bonus points awarded for successful tackles",
    category: "core_all",
  },
  {
    key: "yellow_card",
    label: "Yellow Card",
    desc: "Cautioned by the referee",
    category: "core_all",
  },
  {
    key: "red_card",
    label: "Red Card",
    desc: "Sent off by the referee",
    category: "core_all",
  },
  {
    key: "penalty_miss",
    label: "Penalty Missed",
    desc: "Failed to score from a penalty",
    category: "core_all",
  },
  {
    key: "own_goal",
    label: "Own Goal",
    desc: "Scored an own goal",
    category: "core_all",
  },

  // Core — Goalkeeper only
  {
    key: "saves",
    label: "Saves Bonus",
    desc: "Bonus points awarded for saves made",
    category: "core_gk",
  },
  {
    key: "penalty_save",
    label: "Penalty Save",
    desc: "Saved an opponent penalty",
    category: "core_gk",
  },

  // Core — Goalkeeper (Clean Sheet & Goals Conceded)
  {
    key: "clean_sheet",
    label: "Clean Sheet",
    desc: "No goals conceded while on the pitch",
    category: "core_gk_clean_sheet",
  },
  {
    key: "goals_conceded_threshold",
    label: "Goals Conceded Penalty",
    desc: "Penalty applied after conceding the threshold number of goals",
    category: "core_gk_clean_sheet",
  },
  {
    key: "goals_conceded_each",
    label: "Goals Conceded Extra",
    desc: "Additional penalty for each goal conceded beyond the extra threshold",
    category: "core_gk_clean_sheet",
  },

  // Core — Defender (Clean Sheet & Goals Conceded)
  {
    key: "clean_sheet",
    label: "Clean Sheet",
    desc: "No goals conceded while on the pitch",
    category: "core_def_clean_sheet",
  },
  {
    key: "goals_conceded_threshold",
    label: "Goals Conceded Penalty",
    desc: "Penalty applied after conceding the threshold number of goals",
    category: "core_def_clean_sheet",
  },
  {
    key: "goals_conceded_each",
    label: "Goals Conceded Extra",
    desc: "Additional penalty for each goal conceded beyond the extra threshold",
    category: "core_def_clean_sheet",
  },

  // PPM — All Players
  {
    key: "ppm_successful_dribble",
    label: "Successful Dribble",
    desc: "Beat an opponent with the ball",
    category: "ppm_all",
  },
  {
    key: "ppm_accurate_cross",
    label: "Accurate Cross",
    desc: "Cross that reaches a teammate",
    category: "ppm_all",
  },
  {
    key: "ppm_interception",
    label: "Interception",
    desc: "Cut out an opponent pass",
    category: "ppm_all",
  },
  {
    key: "ppm_block",
    label: "Block",
    desc: "Blocked a shot or pass",
    category: "ppm_all",
  },
  {
    key: "ppm_goal_outside_area",
    label: "Goal Outside Area",
    desc: "Scored from outside the penalty box",
    category: "ppm_all",
  },
  {
    key: "ppm_foul_won",
    label: "Foul Won",
    desc: "Drew a foul from an opponent",
    category: "ppm_all",
  },
  {
    key: "ppm_offside",
    label: "Offside",
    desc: "Caught offside",
    category: "ppm_all",
  },
  {
    key: "ppm_foul_conceded",
    label: "Foul Conceded",
    desc: "Committed a foul",
    category: "ppm_all",
  },
  {
    key: "ppm_error_leading_to_goal",
    label: "Error Leading to Goal",
    desc: "Mistake that directly leads to a goal",
    category: "ppm_all",
  },
  {
    key: "ppm_pass_completion_70",
    label: "Pass Completion Low Band",
    desc: "Pass completion in the low percentage band",
    category: "ppm_all",
  },
  {
    key: "ppm_pass_completion_80",
    label: "Pass Completion Mid Band",
    desc: "Pass completion in the mid percentage band",
    category: "ppm_all",
  },
  {
    key: "ppm_pass_completion_90",
    label: "Pass Completion High Band",
    desc: "Pass completion in the high percentage band",
    category: "ppm_all",
  },

  // PPM — Goalkeeper only
  {
    key: "ppm_successful_claim",
    label: "Successful Claim",
    desc: "Claimed a high ball securely",
    category: "ppm_gk",
  },
  {
    key: "ppm_successful_punch",
    label: "Successful Punch",
    desc: "Punched a dangerous ball clear",
    category: "ppm_gk",
  },
  {
    key: "ppm_keeper_sweep",
    label: "Keeper Sweep",
    desc: "Came off the line to clear danger",
    category: "ppm_gk",
  },
];

// Actions that support a configurable threshold
const THRESHOLD_ACTIONS = [
  "tackles",
  "saves",
  "ppm_pass_completion_70",
  "ppm_pass_completion_80",
  "ppm_pass_completion_90",
];

// ── Section & subcategory layout ──
const SCORING_SECTIONS = [
  {
    key: "core",
    label: "Core Scoring Rules",
    description: "Points awarded for match events during gameplay.",
    subcategories: [
      {
        key: "core_all",
        label: "All Players",
        positions: [null],
        defaultAction: "appearance",
      },
      {
        key: "core_gk",
        label: "Goalkeeper Players Only",
        positions: ["GK"],
        defaultAction: "saves",
      },
      {
        key: "core_gk_clean_sheet",
        label: "Goalkeeper Players — Clean Sheet & Goals Conceded",
        positions: ["GK"],
        defaultAction: "clean_sheet",
      },
      {
        key: "core_def_clean_sheet",
        label: "Defender Players — Clean Sheet & Goals Conceded",
        positions: ["DEF"],
        defaultAction: "clean_sheet",
      },
    ],
  },
  {
    key: "ppm",
    label: "Player Performance Marks (PPM)",
    description:
      "Performance-based marks that contribute to bonus point calculations.",
    subcategories: [
      {
        key: "ppm_all",
        label: "All Players",
        positions: [null],
        defaultAction: "ppm_successful_dribble",
      },
      {
        key: "ppm_gk",
        label: "Goalkeeper Players Only",
        positions: ["GK"],
        defaultAction: "ppm_successful_claim",
      },
    ],
  },
];

const DEFAULT_CONFIG = {
  captain_multiplier: null,
  vice_captain_enabled: true,
  min_minutes_clean_sheet: null,
  min_minutes_appearance_bonus: null,
  min_passes_completion_rate: null,
  goals_conceded_threshold_count: null,
  ppm_tier1_min: null,
  ppm_tier1_max: null,
  ppm_tier1_points: null,
  ppm_tier2_min: null,
  ppm_tier2_max: null,
  ppm_tier2_points: null,
  ppm_tier3_min: null,
  ppm_tier3_points: null,
};

const getDefaultLabel = (actionKey) => {
  const action = PREDEFINED_ACTIONS.find((a) => a.key === actionKey);
  return action ? action.label : actionKey;
};

const getActionDesc = (actionKey) => {
  const action = PREDEFINED_ACTIONS.find((a) => a.key === actionKey);
  return action ? action.desc : "";
};

const getActionsForSubcategory = (subcategoryKey) => {
  return PREDEFINED_ACTIONS.filter((a) => a.category === subcategoryKey);
};

const supportsThreshold = (actionKey) => THRESHOLD_ACTIONS.includes(actionKey);

const generateTempId = () =>
  `temp_${Math.random().toString(36).substring(2, 11)}`;

const generateAllDefaultRules = () => {
  const allRules = [];
  SCORING_SECTIONS.forEach((section) => {
    section.subcategories.forEach((sub) => {
      const actions = getActionsForSubcategory(sub.key);
      actions.forEach((action) => {
        allRules.push({
          tempId: generateTempId(),
          action_key: action.key,
          label: action.label,
          position: sub.positions[0],
          points: null,
          threshold: null,
        });
      });
    });
  });
  return allRules;
};

const ScoringRulesConfigPage = () => {
  const { tournamentCalendarId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tournament, setTournament] = useState(null);
  const [tournamentStatus, setTournamentStatus] = useState("draft");
  const isDraft = tournamentStatus === "draft";
  const [ruleSet, setRuleSet] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [rules, setRules] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [copyFromId, setCopyFromId] = useState("");
  const [expandedSections, setExpandedSections] = useState(["core"]);
  const [selectedSubcategories, setSelectedSubcategories] = useState({
    core: "core_all",
    ppm: "ppm_all",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors([]);
      const rulesResponse = await get(
        `/admin/scoring-rules/rules?tournament_calendar_id=${tournamentCalendarId}`
      );
      if (rulesResponse?.data) {
        setRuleSet(rulesResponse.data.ruleSet);
        if (rulesResponse.data.tournamentStatus)
          setTournamentStatus(rulesResponse.data.tournamentStatus);
        if (rulesResponse.data.ruleSet?.config)
          setConfig(rulesResponse.data.ruleSet.config);

        // If rules exist from DB, use them; otherwise generate all defaults
        if (rulesResponse.data.rules && rulesResponse.data.rules.length > 0) {
          const normalizedRules = rulesResponse.data.rules.map((r) => ({
            ...r,
            position: r.position === "" ? null : r.position,
          }));
          setRules(normalizedRules);
        } else {
          setRules(generateAllDefaultRules());
        }
      } else {
        setRules(generateAllDefaultRules());
      }
      const tournamentsResponse = await get("/admin/scoring-rules");
      if (tournamentsResponse?.data?.tournaments) {
        setTournaments(tournamentsResponse.data.tournaments);
        setTournament(
          tournamentsResponse.data.tournaments.find(
            (t) => t.id === tournamentCalendarId
          )
        );
      }
    } catch (err) {
      console.error("Failed to fetch scoring rules:", err);
      setError("Failed to load scoring rules");
    } finally {
      setLoading(false);
    }
  }, [tournamentCalendarId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConfigChange = (field, value) =>
    setConfig((prev) => ({ ...prev, [field]: value }));

  const findRuleIndex = (rulesList, rule) => {
    if (rule.id) return rulesList.findIndex((r) => r.id === rule.id);
    if (rule.tempId)
      return rulesList.findIndex((r) => r.tempId === rule.tempId);
    return -1;
  };

  const handleRuleChange = (rule, field, value) => {
    setRules((prev) => {
      const idx = findRuleIndex(prev, rule);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === "action_key") {
        updated[idx].label = getDefaultLabel(value);
      }
      return updated;
    });
    setValidationErrors([]);
  };

  const getRuleLocation = (rule) => {
    for (const section of SCORING_SECTIONS) {
      for (const sub of section.subcategories) {
        const actions = getActionsForSubcategory(sub.key);
        if (
          sub.positions.includes(rule.position) &&
          actions.some((a) => a.key === rule.action_key)
        ) {
          return { sectionLabel: section.label, subLabel: sub.label };
        }
      }
    }
    return { sectionLabel: "Unknown Section", subLabel: "Unknown Group" };
  };

  const validateRules = () => {
    const errors = [];

    // Validate config fields
    const configFields = [
      { key: "captain_multiplier", label: "Captain Multiplier" },
      { key: "min_minutes_clean_sheet", label: "Min Minutes for Clean Sheet" },
      {
        key: "min_minutes_appearance_bonus",
        label: "Min Minutes for Appearance Bonus",
      },
      {
        key: "min_passes_completion_rate",
        label: "Min Passes for Completion Rate",
      },
      {
        key: "goals_conceded_threshold_count",
        label: "Goals Conceded Threshold",
      },
      { key: "ppm_tier1_min", label: "Tier 1 — From (Min PPMs)" },
      { key: "ppm_tier1_max", label: "Tier 1 — To (Max PPMs)" },
      { key: "ppm_tier1_points", label: "Tier 1 — Bonus Points" },
      { key: "ppm_tier2_min", label: "Tier 2 — From (Min PPMs)" },
      { key: "ppm_tier2_max", label: "Tier 2 — To (Max PPMs)" },
      { key: "ppm_tier2_points", label: "Tier 2 — Bonus Points" },
      { key: "ppm_tier3_min", label: "Tier 3 — From (Min PPMs)" },
      { key: "ppm_tier3_points", label: "Tier 3 — Bonus Points" },
    ];

    configFields.forEach(({ key, label }) => {
      const val = config[key];
      if (
        val === null ||
        val === undefined ||
        val === "" ||
        Number.isNaN(val)
      ) {
        errors.push(`Configuration "${label}" is required.`);
      }
    });

    // Validate individual rules have points
    rules.forEach((rule) => {
      const { sectionLabel, subLabel } = getRuleLocation(rule);
      const actionLabel = rule.label || getDefaultLabel(rule.action_key);

      // Check that points is set (compulsory) — 0 is allowed, but empty/NaN is not
      if (
        rule.points === null ||
        rule.points === undefined ||
        rule.points === "" ||
        Number.isNaN(rule.points)
      ) {
        errors.push(
          `"${sectionLabel} → ${subLabel} → ${actionLabel}" is missing points. Please enter a value.`
        );
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleCopyFrom = async () => {
    if (!copyFromId) return;
    try {
      setLoading(true);
      const response = await get(
        `/admin/scoring-rules/rules?tournament_calendar_id=${copyFromId}`
      );
      if (response?.data?.rules && response.data.rules.length > 0) {
        const filledRules = response.data.rules.map((r) => ({
          ...r,
          id: undefined,
          tempId: generateTempId(),
          position: r.position === "" ? null : r.position,
        }));
        setRules(filledRules);
        if (response.data.ruleSet?.config) {
          setConfig(response.data.ruleSet.config);
        }
        const sourceTournament = tournaments.find((t) => t.id === copyFromId);
        setSnackbar({
          open: true,
          message: `Rules filled from ${
            sourceTournament?.competition_name || "selected tournament"
          } — click Save Changes to persist`,
          severity: "success",
        });
        setCopyFromId("");
      } else {
        setSnackbar({
          open: true,
          message: "Selected tournament has no rules to copy",
          severity: "warning",
        });
      }
    } catch (err) {
      console.error("Failed to copy rules:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to copy rules",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!validateRules()) {
      setSnackbar({
        open: true,
        message: "Please fix validation errors before saving",
        severity: "error",
      });
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        config,
        rules: rules.map((r) => ({
          id: r.id,
          action_key: r.action_key,
          label: r.label,
          position: r.position,
          points: r.points,
          threshold: r.threshold,
        })),
      };
      let response;
      if (ruleSet) {
        response = await put(`/admin/scoring-rules/${ruleSet.id}`, payload);
      } else {
        response = await post(`/admin/scoring-rules/${tournamentCalendarId}`, {
          ...payload,
          name: `${tournament?.competition_name || "Custom"} Rules`,
        });
      }
      if (response?.data) {
        setSnackbar({
          open: true,
          message: "Scoring rules saved successfully!",
          severity: "success",
        });
        if (!ruleSet) setRuleSet(response.data);
        await fetchData();
        navigate("/dashboard/scoring-rules");
      }
    } catch (err) {
      console.error("Failed to save:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to save",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const getRulesForSubcategory = (subcategory) => {
    return rules.filter(
      (r) =>
        subcategory.positions.includes(r.position) &&
        getActionsForSubcategory(subcategory.key).some(
          (a) => a.key === r.action_key
        )
    );
  };

  const getThresholdHelper = (actionKey, threshold) => {
    if (!threshold) return "";
    switch (actionKey) {
      case "tackles":
        return `${threshold} tackles per point`;
      case "saves":
        return `${threshold} saves per point`;
      case "goals_conceded_threshold":
        return `After ${threshold} goals conceded`;
      case "goals_conceded_each":
        return `Each goal after ${threshold}`;
      case "ppm_pass_completion_70":
        return "Low band threshold";
      case "ppm_pass_completion_80":
        return "Mid band threshold";
      case "ppm_pass_completion_90":
        return "High band threshold";
      case "bonus_tier_1":
        return `From ${threshold} PPMs`;
      case "bonus_tier_2":
        return `From ${threshold} PPMs`;
      case "bonus_tier_3":
        return `From ${threshold} PPMs`;
      default:
        return `Threshold: ${threshold}`;
    }
  };

  const handleAccordionChange = (sectionKey) => {
    setExpandedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((k) => k !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const handleSubcategoryChange = (sectionKey, subcategoryKey) => {
    setSelectedSubcategories((prev) => ({
      ...prev,
      [sectionKey]: subcategoryKey,
    }));
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Scoring Rules Configuration | DreamTeam Admin</title>
      </Helmet>
      <Container maxWidth="xl">
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate("/dashboard/scoring-rules")}
              sx={{ mb: 1 }}
            >
              Back to List
            </Button>
            <Typography variant="h4">
              Scoring Rules — {tournament?.competition_name || "Tournament"}
            </Typography>
          </Box>
          {isDraft && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </Box>

        {!isDraft && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">
              Scoring rules are locked because this tournament is{" "}
              <strong>{tournamentStatus}</strong>. Rules can only be edited
              while the tournament is in draft status.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Please fix the following issues:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationErrors.map((err, i) => (
                <li key={i}>
                  <Typography variant="body2">{err}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {/* ── Copy from tournament ── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              Copy values from another tournament (does not save until you click
              Save Changes)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Copy rules from tournament</InputLabel>
                <Select
                  value={copyFromId}
                  label="Copy rules from tournament"
                  disabled={!isDraft}
                  onChange={(e) => setCopyFromId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a tournament...</em>
                  </MenuItem>
                  {tournaments
                    .filter(
                      (t) =>
                        t.has_scoring_rules && t.id !== tournamentCalendarId
                    )
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.competition_name} ({t.season_label})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyFrom}
                disabled={!isDraft || !copyFromId || loading}
              >
                Copy & Fill
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* ── Rule Set Configuration ── */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Rule Set Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Global settings that apply to all players and all scoring
              calculations.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Captain Multiplier"
                  type="number"
                  required
                  disabled={!isDraft}
                  value={config.captain_multiplier ?? ""}
                  onChange={(e) =>
                    handleConfigChange(
                      "captain_multiplier",
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10)
                    )
                  }
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  helperText="e.g. 2× points"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.vice_captain_enabled}
                      disabled={!isDraft}
                      onChange={(e) =>
                        handleConfigChange(
                          "vice_captain_enabled",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Vice Captain Enabled"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Minutes for Clean Sheet"
                  type="number"
                  required
                  disabled={!isDraft}
                  value={config.min_minutes_clean_sheet ?? ""}
                  onChange={(e) =>
                    handleConfigChange(
                      "min_minutes_clean_sheet",
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10)
                    )
                  }
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  helperText="Player must play this long"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Minutes for Appearance Bonus"
                  type="number"
                  required
                  disabled={!isDraft}
                  value={config.min_minutes_appearance_bonus ?? ""}
                  onChange={(e) =>
                    handleConfigChange(
                      "min_minutes_appearance_bonus",
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10)
                    )
                  }
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  helperText="Minimum minutes to qualify for appearance bonus"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Min Passes for Completion Rate"
                  type="number"
                  required
                  disabled={!isDraft}
                  value={config.min_passes_completion_rate ?? ""}
                  onChange={(e) =>
                    handleConfigChange(
                      "min_passes_completion_rate",
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10)
                    )
                  }
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  helperText="Minimum pass attempts to qualify for completion rate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Goals Conceded Threshold"
                  type="number"
                  required
                  disabled={!isDraft}
                  value={config.goals_conceded_threshold_count ?? ""}
                  onChange={(e) =>
                    handleConfigChange(
                      "goals_conceded_threshold_count",
                      e.target.value === ""
                        ? null
                        : parseInt(e.target.value, 10)
                    )
                  }
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  helperText="Number of goals conceded before penalty applies"
                />
              </Grid>
            </Grid>

            {/* PPM to Bonus Point Conversion */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              PPM to Bonus Point Conversion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Extra bonus points awarded when a player&apos;s total Performance
              Marks (PPMs) fall within these ranges.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Tier 1 */}
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Tier 1 — Low PPM Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="From (Min PPMs)"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier1_min ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier1_min",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Minimum"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="To (Max PPMs)"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier1_max ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier1_max",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Maximum"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Bonus Points"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier1_points ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier1_points",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Awarded"
                    />
                  </Grid>
                </Grid>
              </Card>
              {/* Tier 2 */}
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Tier 2 — Medium PPM Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="From (Min PPMs)"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier2_min ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier2_min",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Minimum"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="To (Max PPMs)"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier2_max ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier2_max",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Maximum"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Bonus Points"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier2_points ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier2_points",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Awarded"
                    />
                  </Grid>
                </Grid>
              </Card>
              {/* Tier 3 */}
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Tier 3 — High PPM Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="From (Min PPMs)"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier3_min ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier3_min",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Minimum"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Bonus Points"
                      type="number"
                      size="small"
                      required
                      disabled={!isDraft}
                      fullWidth
                      value={config.ppm_tier3_points ?? ""}
                      onChange={(e) =>
                        handleConfigChange(
                          "ppm_tier3_points",
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value, 10)
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                      helperText="Awarded"
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>
          </CardContent>
        </Card>

        {/* ── Individual Scoring Rules ── */}
        <Typography variant="h5" sx={{ mb: 1 }}>
          Individual Scoring Rules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define how many points each match event is worth per player position.{" "}
          <span style={{ color: "#d32f2f" }}>
            Points are compulsory for every rule.
          </span>{" "}
          Changes are only saved when you click <strong>Save Changes</strong>.
        </Typography>

        {SCORING_SECTIONS.map((section) => {
          const isExpanded = expandedSections.includes(section.key);
          const selectedSubKey = selectedSubcategories[section.key];
          const selectedSub = section.subcategories.find(
            (sc) => sc.key === selectedSubKey
          );
          const categoryRules = selectedSub
            ? getRulesForSubcategory(selectedSub)
            : [];
          const availableActions = selectedSub
            ? getActionsForSubcategory(selectedSub.key)
            : [];

          return (
            <Accordion
              key={section.key}
              expanded={isExpanded}
              onChange={() => handleAccordionChange(section.key)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <Typography variant="h6">{section.label}</Typography>
                  <Chip
                    label={`${categoryRules.length} rule${
                      categoryRules.length !== 1 ? "s" : ""
                    }`}
                    size="small"
                    color={categoryRules.length > 0 ? "primary" : "default"}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Subcategory selector (if more than one) */}
                {section.subcategories.length > 1 && (
                  <FormControl sx={{ minWidth: 280, mb: 2 }}>
                    <InputLabel>{`Select ${section.label} Group`}</InputLabel>
                    <Select
                      value={selectedSubKey}
                      label={`Select ${section.label} Group`}
                      onChange={(e) =>
                        handleSubcategoryChange(section.key, e.target.value)
                      }
                    >
                      {section.subcategories.map((sub) => (
                        <MenuItem key={sub.key} value={sub.key}>
                          {sub.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {section.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {section.description}
                  </Typography>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%">Action</TableCell>
                        <TableCell width="30%">Label</TableCell>
                        <TableCell width="20%" align="center">
                          Points <span style={{ color: "#d32f2f" }}>*</span>
                        </TableCell>
                        <TableCell width="20%" align="center">
                          Threshold (if applicable)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryRules.map((rule) => (
                        <TableRow
                          key={rule.id || rule.tempId}
                          hover
                          sx={{ "& td": { py: 1.5 } }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {availableActions.find(
                                (a) => a.key === rule.action_key
                              )?.label || rule.action_key}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {getActionDesc(rule.action_key)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              disabled={!isDraft}
                              value={rule.label}
                              onChange={(e) =>
                                handleRuleChange(rule, "label", e.target.value)
                              }
                              fullWidth
                              placeholder="Display name"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              required
                              disabled={!isDraft}
                              error={
                                rule.points === null ||
                                rule.points === undefined ||
                                rule.points === ""
                              }
                              value={rule.points}
                              onChange={(e) =>
                                handleRuleChange(
                                  rule,
                                  "points",
                                  e.target.value === ""
                                    ? null
                                    : parseInt(e.target.value, 10)
                                )
                              }
                              onWheel={(e) => e.target.blur()}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {supportsThreshold(rule.action_key) ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <TextField
                                  type="number"
                                  size="small"
                                  disabled={!isDraft}
                                  value={rule.threshold || ""}
                                  onChange={(e) =>
                                    handleRuleChange(
                                      rule,
                                      "threshold",
                                      e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : null
                                    )
                                  }
                                  onWheel={(e) => e.target.blur()}
                                  sx={{ width: 80 }}
                                  placeholder="None"
                                />
                                {rule.threshold && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {getThresholdHelper(
                                      rule.action_key,
                                      rule.threshold
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default ScoringRulesConfigPage;
